import type { StorageListOrder, StorageReadGrantInput, StorageVisibility } from "@gencow/core";
import { applyAdminAccessMiddleware, type AdminAccessPolicy } from "./server-admin-access.js";
import {
  clampStorageListLimit,
  decodeStorageListCursor,
  encodeStorageListCursor,
  type StorageListCursorPayload,
} from "./storage-system-files-catalog.js";
import { normalizeStorageVisibility } from "./storage-visibility.js";

type RawSqlFn = (sql: string, params?: any[]) => Promise<any[]>;

type StorageLike = {
  store(
    file: File,
    filenameOrOptions?: string | { filename?: string; visibility?: StorageVisibility },
  ): Promise<string>;
  getMeta?(storageId: string): Promise<{ visibility?: StorageVisibility | string } | null>;
  createReadGrant?(
    storageId: string,
    input?: StorageReadGrantInput,
  ): Promise<{ url: string; expiresAt: string }>;
  getPublicUrl?(storageId: string): string;
  setVisibility?(storageId: string, visibility: StorageVisibility): Promise<void>;
  delete?(storageId: string): Promise<void>;
  remove?(storageId: string): Promise<void>;
};

type AdminFilesErrorCode =
  | "FILES_CATALOG_UNAVAILABLE"
  | "FILES_ACCESS_URL_UNAVAILABLE"
  | "FILES_INVALID_CURSOR";

class AdminFilesRouteError extends Error {
  readonly code: AdminFilesErrorCode;
  readonly status: number;

  constructor(code: AdminFilesErrorCode, message: string, options?: { cause?: unknown; status?: number }) {
    super(message);
    this.name = "AdminFilesRouteError";
    this.code = code;
    this.status = options?.status ?? 500;
    this.cause = options?.cause;
  }
}

type RouteRegistrar = {
  use?(path: string, handler: any): void;
  get(path: string, handler: (context: any) => Promise<unknown> | unknown): void;
  post(path: string, handler: (context: any) => Promise<unknown> | unknown): void;
  patch(path: string, handler: (context: any) => Promise<unknown> | unknown): void;
  delete(path: string, handler: (context: any) => Promise<unknown> | unknown): void;
};

const CREATE_FILES_TABLE_SQL = `
    DO $$
    BEGIN
        IF NOT EXISTS (
            SELECT 1 FROM information_schema.tables
            WHERE table_schema = current_schema()
            AND table_name = '_system_files'
        ) THEN
            CREATE TABLE _system_files (
                id SERIAL PRIMARY KEY,
                storage_id TEXT NOT NULL,
                name TEXT NOT NULL,
                size TEXT NOT NULL,
                type TEXT NOT NULL DEFAULT 'application/octet-stream',
                visibility TEXT NOT NULL DEFAULT 'private',
                uploaded_by TEXT DEFAULT 'dashboard',
                created_at TIMESTAMP DEFAULT NOW()
            );
        END IF;
    END$$;
`;

let filesTableInitialized = false;
let filesTableHasVisibilityColumn = false;

export function resetFilesTableInitializationForTests(): void {
  filesTableInitialized = false;
  filesTableHasVisibilityColumn = false;
}

export async function ensureFilesTable(rawSql: RawSqlFn): Promise<{ hasVisibilityColumn: boolean }> {
  if (filesTableInitialized) return { hasVisibilityColumn: filesTableHasVisibilityColumn };
  await rawSql(CREATE_FILES_TABLE_SQL);
  const visibilityColumns = await rawSql(
    `SELECT 1
       FROM information_schema.columns
      WHERE table_schema = current_schema()
        AND table_name = '_system_files'
        AND column_name = 'visibility'
      LIMIT 1`,
  );
  filesTableHasVisibilityColumn = visibilityColumns.length > 0;
  if (!filesTableHasVisibilityColumn) {
    try {
      await rawSql(
        `ALTER TABLE _system_files ADD COLUMN IF NOT EXISTS visibility TEXT NOT NULL DEFAULT 'private'`,
      );
      filesTableHasVisibilityColumn = true;
    } catch {
      filesTableHasVisibilityColumn = false;
    }
  }
  filesTableInitialized = true;
  return { hasVisibilityColumn: filesTableHasVisibilityColumn };
}

export function normalizeUploadedBy(value: string | undefined): string {
  const validSources = new Set(["cli", "dashboard", "api"]);
  return validSources.has(value || "") ? (value as string) : "dashboard";
}

export function isValidStorageId(storageId: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(storageId);
}

function parseStorageVisibility(value: unknown): StorageVisibility | null {
  return value === "public" || value === "private" ? value : null;
}

type AdminFilesPageQuery = {
  paginated: boolean;
  limit: number;
  cursor: StorageListCursorPayload | null;
  order: StorageListOrder;
};

function invalidFilesCursor(message = "Invalid files cursor", cause?: unknown): AdminFilesRouteError {
  return new AdminFilesRouteError("FILES_INVALID_CURSOR", message, { cause, status: 400 });
}

function parseAdminFilesPageQuery(c: any): AdminFilesPageQuery {
  const limitRaw = c.req.query("limit");
  const cursorRaw = c.req.query("cursor");
  const orderRaw = c.req.query("order");
  const pageRaw = c.req.query("page");
  const paginated =
    limitRaw !== undefined || cursorRaw !== undefined || orderRaw !== undefined || pageRaw !== undefined;

  if (pageRaw !== undefined && pageRaw !== "" && pageRaw !== "1") {
    throw invalidFilesCursor("Invalid files page");
  }

  let order: StorageListOrder = "desc";
  if (orderRaw === "asc" || orderRaw === "desc") {
    order = orderRaw;
  } else if (orderRaw !== undefined && orderRaw !== "") {
    throw invalidFilesCursor("Invalid files order");
  }
  const limit = clampStorageListLimit(limitRaw === undefined ? undefined : Number(limitRaw));
  let cursor: StorageListCursorPayload | null = null;
  if (cursorRaw) {
    try {
      cursor = decodeStorageListCursor(cursorRaw, order);
    } catch (error) {
      throw invalidFilesCursor("Invalid files cursor", error);
    }
  }

  return { paginated, limit, cursor, order };
}

async function buildFileAccessUrls(
  storage: StorageLike | undefined,
  storageId: string,
  visibility: StorageVisibility,
): Promise<{ url: string; download_url: string }> {
  const publicUrl = storage?.getPublicUrl?.(storageId) ?? `/api/storage/${storageId}`;
  if (visibility === "public" || !storage?.createReadGrant) {
    return { url: publicUrl, download_url: publicUrl };
  }

  const [inlineGrant, attachmentGrant] = await Promise.all([
    storage.createReadGrant(storageId, { ttlSeconds: 300, disposition: "inline" }),
    storage.createReadGrant(storageId, { ttlSeconds: 300, disposition: "attachment" }),
  ]).catch((error) => {
    throw new AdminFilesRouteError("FILES_ACCESS_URL_UNAVAILABLE", "File access URL unavailable", {
      cause: error,
    });
  });
  return { url: inlineGrant.url, download_url: attachmentGrant.url };
}

async function deleteStorageObject(storage: StorageLike, storageId: string): Promise<void> {
  const deleteFn =
    typeof storage.remove === "function"
      ? storage.remove.bind(storage)
      : typeof storage.delete === "function"
        ? storage.delete.bind(storage)
        : null;
  if (!deleteFn) return;
  try {
    await deleteFn(storageId);
  } catch {
    throw new Error("Storage object delete failed");
  }
}

export function registerAdminFileRoutes(params: {
  admin: RouteRegistrar;
  rawSql: RawSqlFn;
  storage?: StorageLike;
  accessPolicy?: AdminAccessPolicy;
}): void {
  applyAdminAccessMiddleware(params.admin, params.accessPolicy);

  params.admin.get("/files", async (c) => {
    try {
      const tableInfo = await ensureFilesTable(params.rawSql);
      const pageQuery = parseAdminFilesPageQuery(c);
      const sqlParams: unknown[] = [];
      let whereClause = "WHERE storage_id IS NOT NULL";
      let limitClause = "";
      let orderClause = "ORDER BY created_at DESC";

      if (pageQuery.paginated) {
        const direction = pageQuery.order === "asc" ? "ASC" : "DESC";
        const comparator = pageQuery.order === "asc" ? ">" : "<";
        if (pageQuery.cursor) {
          sqlParams.push(pageQuery.cursor.t, pageQuery.cursor.s);
          whereClause += ` AND (created_at, storage_id) ${comparator} ($1::timestamp, $2)`;
        }
        sqlParams.push(pageQuery.limit + 1);
        limitClause = `LIMIT $${sqlParams.length}`;
        orderClause = `ORDER BY created_at ${direction}, storage_id ${direction}`;
      }

      const rows = await params.rawSql(
        tableInfo.hasVisibilityColumn
          ? `SELECT id, storage_id, name, size, type, visibility, uploaded_by, created_at${
              pageQuery.paginated ? ", created_at::text AS created_at_text" : ""
            }
               FROM _system_files
              ${whereClause}
              ${orderClause}
              ${limitClause}`
          : `SELECT id, storage_id, name, size, type, uploaded_by, created_at${
              pageQuery.paginated ? ", created_at::text AS created_at_text" : ""
            }
               FROM _system_files
              ${whereClause}
              ${orderClause}
              ${limitClause}`,
        sqlParams,
      );
      const pageRows = pageQuery.paginated ? rows.slice(0, pageQuery.limit) : rows;
      const files = await Promise.all(
        pageRows.map(async (row) => {
          const storageId = String(row.storage_id);
          let visibility = normalizeStorageVisibility(row.visibility);
          if (!tableInfo.hasVisibilityColumn && params.storage?.getMeta) {
            const meta = await params.storage.getMeta(storageId).catch((error) => {
              throw new AdminFilesRouteError(
                "FILES_CATALOG_UNAVAILABLE",
                "File catalog metadata unavailable",
                { cause: error },
              );
            });
            visibility = normalizeStorageVisibility(meta?.visibility);
          }
          const urls = await buildFileAccessUrls(params.storage, storageId, visibility);
          const { created_at_text: _createdAtText, ...file } = row;
          return { ...file, visibility, ...urls };
        }),
      );
      if (pageQuery.paginated) {
        const last = pageRows[pageRows.length - 1];
        const nextCursor =
          rows.length > pageQuery.limit && last
            ? encodeStorageListCursor({
                v: 1,
                o: pageQuery.order,
                t: String(last.created_at_text ?? last.created_at ?? ""),
                s: String(last.storage_id),
              })
            : null;
        return c.json({ files, nextCursor, limit: pageQuery.limit, order: pageQuery.order });
      }
      return c.json(files);
    } catch (error: unknown) {
      const routeError =
        error instanceof AdminFilesRouteError
          ? error
          : new AdminFilesRouteError("FILES_CATALOG_UNAVAILABLE", "File catalog unavailable", {
              cause: error,
            });
      const cause = routeError.cause instanceof Error ? routeError.cause.message : String(routeError.cause ?? "");
      console.warn(
        `[admin-files] GET /files failed code=${routeError.code} message=${routeError.message}${cause ? ` cause=${cause}` : ""}`,
      );
      return c.json({ error: routeError.message, code: routeError.code }, routeError.status === 400 ? 400 : 500);
    }
  });

  params.admin.post("/files/upload", async (c) => {
    if (!params.storage) {
      return c.json({ error: "Storage not initialized" }, 500);
    }
    try {
      const tableInfo = await ensureFilesTable(params.rawSql);
      const body = await c.req.parseBody();
      const file = body.file;
      if (!file || !(file instanceof File)) {
        return c.json({ error: "No file provided. Send as multipart with field name 'file'" }, 400);
      }

      const visibility = parseStorageVisibility(body.visibility) ?? "private";
      const storageId = await params.storage.store(file, { filename: file.name, visibility });
      const uploadedBy = normalizeUploadedBy(c.req.header("x-uploaded-by"));
      try {
        if (tableInfo.hasVisibilityColumn) {
          await params.rawSql(
            `UPDATE _system_files SET uploaded_by = $1, visibility = $2 WHERE storage_id = $3`,
            [uploadedBy, visibility, storageId],
          );
        } else {
          await params.rawSql(`UPDATE _system_files SET uploaded_by = $1 WHERE storage_id = $2`, [
            uploadedBy,
            storageId,
          ]);
        }
      } catch {
        /* ignore */
      }

      const urls = await buildFileAccessUrls(params.storage, storageId, visibility);
      return c.json(
        {
          storageId,
          name: file.name,
          size: file.size,
          type: file.type,
          visibility,
          ...urls,
        },
        201,
      );
    } catch (error: any) {
      return c.json({ error: error.message || "Upload failed" }, 500);
    }
  });

  params.admin.patch("/files/:storageId/visibility", async (c) => {
    if (!params.storage) {
      return c.json({ error: "Storage not initialized" }, 500);
    }
    try {
      await ensureFilesTable(params.rawSql);
      const storageId = c.req.param("storageId");
      if (!storageId) {
        return c.json({ error: "storageId is required" }, 400);
      }
      if (!isValidStorageId(storageId)) {
        return c.json({ error: "Invalid storageId format" }, 400);
      }

      const body = await c.req.json().catch(() => ({}));
      const visibility = parseStorageVisibility(body?.visibility);
      if (!visibility) {
        return c.json({ error: "visibility must be 'private' or 'public'" }, 400);
      }

      const rows = await params.rawSql(
        `SELECT id, storage_id, name FROM _system_files WHERE storage_id = $1`,
        [storageId],
      );
      if (rows.length === 0) {
        return c.json({ error: "File not found" }, 404);
      }

      if (typeof params.storage.setVisibility === "function") {
        await params.storage.setVisibility(storageId, visibility);
      } else {
        await params.rawSql(`UPDATE _system_files SET visibility = $1 WHERE storage_id = $2`, [
          visibility,
          storageId,
        ]);
      }

      const urls = await buildFileAccessUrls(params.storage, storageId, visibility);
      return c.json({ success: true, storageId, visibility, ...urls });
    } catch (error: any) {
      return c.json({ error: error.message || "Visibility update failed" }, 500);
    }
  });

  params.admin.delete("/files/:storageId", async (c) => {
    if (!params.storage) {
      return c.json({ error: "Storage not initialized" }, 500);
    }
    try {
      await ensureFilesTable(params.rawSql);
      const storageId = c.req.param("storageId");
      if (!storageId) {
        return c.json({ error: "storageId is required" }, 400);
      }
      if (!isValidStorageId(storageId)) {
        return c.json({ error: "Invalid storageId format" }, 400);
      }

      const rows = await params.rawSql(
        `SELECT id, storage_id, name FROM _system_files WHERE storage_id = $1`,
        [storageId],
      );
      if (rows.length === 0) {
        return c.json({ error: "File not found" }, 404);
      }

      await deleteStorageObject(params.storage, storageId);

      await params.rawSql(`DELETE FROM _system_files WHERE storage_id = $1`, [storageId]);
      return c.json({ success: true, deleted: storageId });
    } catch (error: any) {
      return c.json({ error: error.message || "Delete failed" }, 500);
    }
  });
}
